import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../supabaseClient';

const AMBER     = '#F5A623';
const NAVY_MID  = '#132233';
const WHITE     = '#FFFFFF';
const FONT_BODY = "'DM Sans', sans-serif";

const PANEL_WIDTH   = 300;
const EDGE_GUTTER   = 12;

function timeAgo(dateStr, t) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return t('notifications.justNow');
  if (m < 60) return t('notifications.minutesAgo', { count: m });
  const h = Math.floor(m / 60);
  if (h < 24) return t('notifications.hoursAgo', { count: h });
  return t('notifications.daysAgo', { count: Math.floor(h / 24) });
}

export default function NotificationBell({ userId, onNavigate }) {
  const { t } = useTranslation();
  const [open, setOpen]                   = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount]     = useState(0);
  const [panelPos, setPanelPos]           = useState(null);
  const panelRef  = useRef(null);
  const buttonRef = useRef(null);

  // The panel used to be absolute with right: 0, i.e. right-aligned to the bell. That only fits
  // when the bell sits at least PANEL_WIDTH from the left edge -- on a phone, the settings and
  // avatar icons to its right push it further in, and the panel ran off the left of the screen.
  // It's now fixed and placed in viewport coordinates: still right-aligned under the bell where
  // there's room (so desktop is unchanged), but clamped so both edges keep EDGE_GUTTER clear.
  // Safe as fixed because none of the dashboard navs that host the bell set a transform or filter.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const vw = document.documentElement.clientWidth;
      const width = Math.min(PANEL_WIDTH, vw - EDGE_GUTTER * 2);
      const left = Math.min(Math.max(rect.right - width, EDGE_GUTTER), vw - EDGE_GUTTER - width);
      setPanelPos({ top: rect.bottom + 10, left, width });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  useEffect(() => {
    if (!userId) return;
    supabase
      .from('notifications')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(10)
      .then(({ data }) => {
        const notifs = data || [];
        setNotifications(notifs);
        setUnreadCount(notifs.filter(n => !n.read).length);
      });
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    const channel = supabase
      .channel(`notifications:${userId}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${userId}`,
      }, (payload) => {
        setNotifications(prev => [payload.new, ...prev]);
        if (!payload.new.read) setUnreadCount(prev => prev + 1);
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [userId]);

  useEffect(() => {
    if (!open) return;
    const handleOutside = e => {
      if (panelRef.current && !panelRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [open]);

  const markRead = async (n) => {
    if (!n.read) {
      await supabase.from('notifications').update({ read: true }).eq('id', n.id);
      setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, read: true } : x));
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
    if (n.link_to && onNavigate) {
      try {
        const link = JSON.parse(n.link_to);
        if (link.tab) onNavigate(link.tab, link);
      } catch {}
    } else if (onNavigate && n.tab) {
      onNavigate(n.tab);
    }
    setOpen(false);
  };

  const markAllRead = async () => {
    if (!notifications.some(n => !n.read)) return;
    await supabase.from('notifications').update({ read: true }).eq('user_id', userId).eq('read', false);
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  return (
    <div ref={panelRef} style={{ position: 'relative' }}>
      <button
        ref={buttonRef}
        onClick={() => setOpen(o => !o)}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative', overflow: 'visible',
        }}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={WHITE} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: '-2px', right: '-2px',
            background: '#DC2626', color: WHITE, borderRadius: '999px',
            fontSize: '9px', fontWeight: 700, lineHeight: 1,
            minWidth: '15px', height: '15px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 3px', zIndex: 1, pointerEvents: 'none',
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && panelPos && (
        <div style={{
          position: 'fixed', top: panelPos.top, left: panelPos.left, zIndex: 1000,
          width: panelPos.width, backgroundColor: NAVY_MID,
          border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5)', overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '11px 14px', borderBottom: '1px solid rgba(255,255,255,0.07)',
          }}>
            <span style={{ fontSize: '0.8rem', fontWeight: 700, color: WHITE, fontFamily: FONT_BODY }}>
              {t('notifications.title')}
            </span>
            {notifications.some(n => !n.read) && (
              <button
                onClick={markAllRead}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: AMBER, fontSize: '0.75rem', fontWeight: 600, fontFamily: FONT_BODY, padding: 0 }}
              >{t('notifications.markAllRead')}</button>
            )}
          </div>

          <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
            {notifications.length === 0 ? (
              <div style={{ padding: '24px 14px', textAlign: 'center', color: 'rgba(255,255,255,0.35)', fontSize: '0.84rem', fontFamily: FONT_BODY }}>
                {t('notifications.empty')}
              </div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  onClick={() => markRead(n)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '11px 14px', minHeight: '44px',
                    backgroundColor: n.read ? 'transparent' : 'rgba(245,166,35,0.06)',
                    borderBottom: '1px solid rgba(255,255,255,0.04)',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{
                    width: '7px', height: '7px', borderRadius: '50%',
                    flexShrink: 0, marginTop: '6px',
                    backgroundColor: n.read ? 'rgba(255,255,255,0.18)' : AMBER,
                  }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ fontSize: '0.82rem', color: n.read ? 'rgba(255,255,255,0.5)' : WHITE, fontFamily: FONT_BODY, lineHeight: 1.45, margin: 0 }}>
                      {n.message}
                    </p>
                    <p style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.28)', fontFamily: FONT_BODY, margin: '3px 0 0 0' }}>
                      {timeAgo(n.created_at, t)}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
